import { base64ToBlob } from './imageCompressor';

// The live chat API takes a message's attachments as file parts of a
// multipart/form-data request (services-sure: channel_route.post_thread_message), so
// each file travels once and in binary, and the API can refuse one that is too large
// before reading it. The rest of the message goes as JSON in the `message` field.
// A backend whose chat settings lack `multipartUploads` takes attachments only as
// base64 in the JSON.

// Where a message keeps its attachments and the copies made of them for display.
const ATTACHMENT_FIELDS = new Set(['images', 'multipleFiles', 'base64File', 'audioDataUrl']);

const isLocalData = (value) =>
  typeof value === 'string' && /^(data:[^,]*;base64,|blob:(https?:\/\/|null\/))/i.test(value);

// The file parts of a message, as [field, data or blob URL, file name].
export const messageAttachments = (msg) => {
  switch (msg?.typeOfMedia) {
    case 'image':
      return (Array.isArray(msg.images) ? msg.images : [])
        .filter((img) => img?.base64)
        .map((img, idx) => ['images', img.base64, `image-${idx + 1}`]);
    case 'file':
    case 'video':
      return msg.base64File ? [['file', msg.base64File, msg.fileName || 'file']] : [];
    case 'audio':
    case 'audio_message':
      return msg.audioDataUrl ? [['audio', msg.audioDataUrl, 'recording']] : [];
    default:
      return [];
  }
};

// The message without its attachments, and without the data and blob URLs it keeps
// for display, which could carry another copy of a file. The visitor's text is
// kept whatever it says.
export const messageFields = (msg) =>
  Object.fromEntries(
    Object.entries(msg).filter(
      ([key, value]) => key === 'text' || (!ATTACHMENT_FIELDS.has(key) && !isLocalData(value)),
    ),
  );

export const blobFromSource = async (source) =>
  source.startsWith('blob:') ? (await fetch(source)).blob() : base64ToBlob(source);

// The multipart form for a message with attachments, or null for one without.
export const buildMessageForm = async (msg, toBlob = blobFromSource) => {
  const attachments = messageAttachments(msg);
  if (!attachments.length) return null;

  const form = new FormData();
  form.append('message', JSON.stringify(messageFields(msg)));
  for (const [field, source, name] of attachments) {
    form.append(field, await toBlob(source), name);
  }
  return form;
};
